import { Injectable, inject } from '@angular/core';
import { HttpService, UrlService } from '@boels/helpers';
import { CartUrlEnum, PaymentDetails, UserEnum } from '@boels/models';
import { filter, Observable, switchMap, tap, withLatestFrom } from 'rxjs';
import { ApiContextService, CartManagementService } from '../../services';

@Injectable({
  providedIn: 'any',
})
export class CheckoutPaymentGatewayService {
  private readonly httpService = inject(HttpService);
  private urlService = inject(UrlService);
  private apiContextService = inject(ApiContextService);
  private cartManagementService = inject(CartManagementService);

  setPaymentDetails(
    paymentDetails: PaymentDetails,
  ): Observable<PaymentDetails> {
    return this.apiContextService.getContext().pipe(
      filter(([baseSite]) => !!baseSite),
      withLatestFrom(
        this.cartManagementService.getCartUser(),
        this.cartManagementService.getCartId(),
      ),
      switchMap(([[baseSite, language, currency], cartUser, cartId]) => {
        const params = [
          ['lang', language],
          ['curr', currency],
          ['fields', 'FULL'],
        ];

        const url = this.urlService.combineUri(
          baseSite.uid,
          this.cartManagementService.getCartUrl(
            cartUser.type,
            CartUrlEnum.CARTS,
          ),
          cartId,
          'paymentdetails',
        );
        const httpHeaders = this.cartManagementService.getHttpHeaders(cartUser);
        return this.httpService.post<PaymentDetails>(
          url,
          paymentDetails,
          params,
          httpHeaders,
        );
      }),
    );
  }

  setPaymentInfo(paymentDetails: PaymentDetails): Observable<PaymentDetails> {
    return this.apiContextService.getContext().pipe(
      filter(([baseSite]) => !!baseSite),
      withLatestFrom(
        this.cartManagementService.getCartUser(),
        this.cartManagementService.getCartId(),
      ),
      switchMap(([[baseSite, language, currency], cartUser, cartId]) => {
        const params = [
          ['lang', language],
          ['curr', currency],
        ];

        const paymentInfoSegments = (userType: UserEnum) => {
          if (userType === UserEnum.B2C_AUTHENTICATED) return ['paymentinfo'];
          return [cartId, 'guestPaymentInfo'];
        };

        const url = this.urlService.combineUri(
          baseSite.uid,
          this.cartManagementService.getCartUrl(
            cartUser.type,
            CartUrlEnum.CARTS,
          ),
          ...paymentInfoSegments(cartUser.type),
        );
        const httpHeaders = this.cartManagementService.getHttpHeaders(cartUser);
        return this.httpService
          .post<PaymentDetails>(url, paymentDetails, params, httpHeaders)
          .pipe(
            tap(() => {
              if (cartUser.type === UserEnum.ANONYMOUS)
                this.cartManagementService.switchAnonymousToGuestCart(baseSite);
            }),
          );
      }),
    );
  }
}
